/** PROTOTYPE guided flow shell -- THROWAWAY.
 *  A left step-rail (Project -> Spreads -> Configure -> Services -> Review) you can
 *  follow in order or jump around in. Prices every spread + the project services from
 *  the engine (via SpreadPriceProbes / useServicesPrice) regardless of the active step. */
import React, { useState, useCallback } from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented } from './protoAtoms';
import { ProjectState, money } from './spreadSplitData';
import { SpreadPriceProbes, OnSpreadState } from './SpreadPriceProbe';
import { SpreadPriceState } from './useSpreadPrice';
import { useServicesPrice } from './useServicesPrice';
import { spreadPricingMap, realProjectTotal, pricingPending } from './spreadRollup';
import { WeldMode, weldingMonths } from './spreadPricingApi';
import { StepDefend } from './StepDefend';
import { ProjectDetailsCard } from './ProjectDetailsCard';
import { StepSpreads } from './StepSpreads';
import { StepConfigure } from './StepConfigure';
import { ProjectSettings } from './ProjectSettings';
import { SpreadDocsView } from './SpreadDocsView';
import { SpreadSaveButton, SaveStatus } from './SpreadSaveButton';

const STEPS = [
  { key: 'project', label: 'Project', hint: 'Customer & reference' },
  { key: 'spreads', label: 'Spreads', hint: 'How many & sizes' },
  { key: 'configure', label: 'Configure', hint: 'Modules per spread' },
  { key: 'services', label: 'Services', hint: 'Platform services & rates' },
  { key: 'defend', label: 'Defend', hint: 'Price & cost drivers' },
  { key: 'review', label: 'Review', hint: 'Totals & documents' },
] as const;
type StepKey = typeof STEPS[number]['key'];

const railTotal: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${CRC.heroRed}`, borderRadius: 12, padding: '12px 14px', marginBottom: 8 };
const stepPill = (on: boolean): React.CSSProperties => ({ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', background: on ? CRC.heroRed10 : '#fff', border: `1px solid ${on ? CRC.heroRed20 : CRC.border}` });
const numBadge = (on: boolean, done: boolean): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, fontSize: 11, fontWeight: 700, fontFamily: 'Funnel Sans', color: on || done ? '#fff' : CRC.heritageGrey, background: on ? CRC.readableRed : done ? CRC.blueprintBlue : CRC.pageBg, border: `1px solid ${on || done ? 'transparent' : CRC.border}` });
const navBtn = (disabled: boolean): React.CSSProperties => ({ padding: '9px 16px', borderRadius: 8, border: `1px solid ${CRC.border}`, background: '#fff', color: disabled ? CRC.heritageGrey : CRC.blueprintBlue, fontWeight: 600, fontSize: 13, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1 });
const navBtnPrimary: React.CSSProperties = { padding: '9px 18px', borderRadius: 8, border: 'none', background: CRC.readableRed, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };

interface Props {
  project: ProjectState;
  setProject: (p: ProjectState) => void;
  /** Persists the project. Omitted on the prototype route, which has no saving. */
  onSave?: (totalPrice: number) => Promise<void>;
  saveLabel?: string;
  /** Save on every step change. On once the proposal has been saved at least once. */
  autoSave?: boolean;
}

export const SpreadFlow: React.FC<Props> = ({ project, setProject, onSave, saveLabel = 'Save proposal', autoSave = false }) => {
  const [step, setStep] = useState<StepKey>('project');
  const [priceStates, setPriceStates] = useState<Record<string, SpreadPriceState>>({});
  const onState: OnSpreadState = useCallback((id, st) => setPriceStates((prev) => (prev[id] === st ? prev : { ...prev, [id]: st })), []);
  const [weldMode, setWeldMode] = useState<WeldMode>('live');
  const [mobilizing, setMobilizing] = useState(true);

  const [saveState, setSaveState] = useState<SaveStatus>({ status: 'idle' });

  const servicesState = useServicesPrice(project);
  const servicesResult = servicesState.status === 'done' ? servicesState.result : null;
  const servicesTotal = servicesResult?.total ?? 0;
  const pricingById = spreadPricingMap(project, priceStates);
  const grand = realProjectTotal(project, priceStates, servicesTotal);
  const pending = pricingPending(project, priceStates);
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  // Saving is never blocked on pricing. The project is what must not be lost;
  // the total is derived and recalculated whenever the proposal is reopened, so
  // a spread that is still loading (or whose pricing call failed) must not stop
  // the work being written down.
  const runSave = async () => {
    if (!onSave) return;
    setSaveState({ status: 'saving' });
    try {
      await onSave(grand);
      setSaveState({ status: 'saved', at: new Date().toLocaleTimeString() });
    } catch (err) {
      setSaveState({ status: 'error', message: err instanceof Error ? err.message : 'Could not save the proposal.' });
    }
  };

  // Moving between steps saves what the current step captured, so leaving a
  // step never loses it. Only once the proposal exists: before that there is
  // nothing to overwrite and each step would create another proposal.
  const goToStep = (next: StepKey) => {
    setStep(next);
    if (autoSave) void runSave();
  };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto' }}>
      {/* Prices every spread from the engine in parallel, regardless of the active step. */}
      <SpreadPriceProbes project={project} mode={weldMode} mobilizing={mobilizing} onState={onState} />

      <div style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 22, alignItems: 'start' }}>
        {/* Step rail */}
        <div style={{ position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={railTotal}>
            <div style={{ fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase', color: CRC.heritageGrey }}>Project total</div>
            <div style={{ fontFamily: 'Funnel Sans', fontSize: 22, fontWeight: 700, color: CRC.readableRed }}>{money(grand)}</div>
            {pending > 0 && <div style={{ fontSize: 10.5, color: CRC.heritageGrey }}>{pending} spread{pending === 1 ? '' : 's'} updating…</div>}
          </div>
          {onSave && <SpreadSaveButton label={saveLabel} state={saveState} onClick={runSave} />}
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => goToStep(s.key)} style={stepPill(s.key === step)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={numBadge(s.key === step, i < stepIndex)}>{i + 1}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: s.key === step ? CRC.readableRed : CRC.blueprintBlue }}>{s.label}</div>
                  <div style={{ fontSize: 10.5, color: CRC.heritageGrey }}>{s.hint}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Step body */}
        <div>
          <div style={{ fontFamily: 'Funnel Sans', fontSize: 20, fontWeight: 700, color: CRC.blueprintBlue }}>{STEPS[stepIndex].label}</div>
          <div style={{ fontSize: 12.5, color: CRC.heritageGrey, marginBottom: 16 }}>{STEPS[stepIndex].hint}</div>

          {step === 'project' && <ProjectDetailsCard project={project} setProject={setProject} />}
          {step === 'spreads' && <StepSpreads project={project} setProject={setProject} priceStates={priceStates} />}
          {step === 'configure' && <StepConfigure project={project} setProject={setProject} priceStates={priceStates} weldMode={weldMode} setWeldMode={setWeldMode} mobilizing={mobilizing} setMobilizing={setMobilizing} />}
          {step === 'services' && <ProjectSettings project={project} setProject={setProject} services={servicesState} />}
          {step === 'defend' && (
            <StepDefend project={project} priceStates={priceStates} servicesTotal={servicesTotal}
              opts={{ mode: weldMode, mobilizing }} baseMonths={weldingMonths} pending={pending} />
          )}
          {step === 'review' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Label>Document style</Label>
                <Segmented value={project.reporting} onChange={(v) => setProject({ ...project, reporting: v as ProjectState['reporting'] })}
                  options={[{ value: 'project', label: 'Project (blended)' }, { value: 'spread', label: 'Per spread' }]} />
              </div>
              <SpreadDocsView project={project} pricingById={pricingById} services={servicesResult} />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 22 }}>
            <button onClick={() => goToStep(STEPS[Math.max(0, stepIndex - 1)].key)} disabled={stepIndex === 0} style={navBtn(stepIndex === 0)}>← Back</button>
            {stepIndex < STEPS.length - 1 && <button onClick={() => goToStep(STEPS[stepIndex + 1].key)} style={navBtnPrimary}>Next →</button>}
          </div>
        </div>
      </div>
    </div>
  );
};
